import { Box, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';

import { ShipmentStop } from '@/common/apis/dtos';
import { getMaxActivityDate } from '@/common/utilities/booking';
import { IsoToDate } from '@/common/utilities/display-utilities';
import { InformationDisplay } from '@/common/components/information-display/InformationDisplay';
import { StreetAddress } from '@/common/components/street-address/StreetAddress';
import { StopStyle, StopType } from '@/common/constants/booking-types';

export interface GridItemProps {
  label: string;
  text: string;
  isVisible: boolean;
}

export function GridItem({ label, text, isVisible }: GridItemProps) {
  if (!isVisible) {
    return null;
  }

  return (
    <Grid item minWidth={240}>
      <InformationDisplay label={label} text={text} />
    </Grid>
  );
}

export interface StopProgressProps {
  stop: ShipmentStop;
  pickupReadyByDate?: string;
}

export function StopProgress({ stop, pickupReadyByDate }: StopProgressProps) {
  const translate = useTranslation();

  const getStopTypeLabel = (enumType: typeof StopType, type?: number) =>
    typeof type === 'undefined' || !enumType[type] ? '-' : translate(enumType[type]);

  const displayDate = (date?: string) => date ? IsoToDate(date) : '-';

  const address = stop.location?.physicalAddress ?? {};
  const addressName = address.name || stop.location?.name;
  const stopNumber = stop.stopNumber;
  const activities = stop.activities ?? [];

  const isPickupStop = stop.type === StopType['Pickup From'];
  const isDeliverStop = stop.type === StopType['Deliver To'];
  const isTransitionalStop = !(isPickupStop || isDeliverStop);
  const hasCutOffStyle =
    stop.style === StopStyle.Port ||
    stop.style === StopStyle.Airport ||
    stop.style === StopStyle.Ramp ||
    stop.style === StopStyle.Terminal;

  const requestedDate = getMaxActivityDate(activities, 'requestedDateTime');
  const estimatedArrivalDate = getMaxActivityDate(activities, 'eta');
  const estimatedDepartureDate = getMaxActivityDate(activities, 'etd');
  const estimatedDeliveryDate = getMaxActivityDate(activities, 'displayEta') || getMaxActivityDate(activities, 'eta');
  const departedDate = getMaxActivityDate(activities, 'departureDateTime');
  const arrivedDate = getMaxActivityDate(activities, 'arrivedDateTime');
  const schedPickupStartDate = getMaxActivityDate(activities, 'openDateTime');
  const schedPickupEndDate = getMaxActivityDate(activities, 'closeDateTime');

  const cutOffDate = isPickupStop && hasCutOffStyle ? estimatedArrivalDate : undefined;
  const cargoReadyByDate = pickupReadyByDate;

  // Show variations of the schedule date if one or both dates are present
  let schedDateRange;
  if (schedPickupStartDate && schedPickupEndDate) {
    schedDateRange = displayDate(schedPickupStartDate) + ' \u2013 ' + displayDate(schedPickupEndDate);
  } else if (schedPickupStartDate && !schedPickupEndDate) {
    schedDateRange = displayDate(schedPickupStartDate);
  } else if (!schedPickupStartDate && schedPickupEndDate) {
    schedDateRange = displayDate(schedPickupEndDate);
  } else {
    schedDateRange = '-';
  }

  return (
    <Box className="stop-info" width={'100%'}>
      <Grid container rowGap={3} padding={2} data-testid={`stop-progress-${stopNumber}`}>
        <Grid item width={250} paddingRight={2}>
          <InformationDisplay spacing={1} label={getStopTypeLabel(StopType, stop.type)}
            component={
              <Stack>
                <Typography className="fs-mask" variant="subtitle2" data-testid={`stop-progress-${stopNumber}-name`}>
                  {addressName}
                </Typography>

                <Typography component="div" className="fs-mask">
                  {StreetAddress({ address })}
                </Typography>

                <Typography variant="body1">
                  {address.city}, {address.stateProvince} {address.country}
                </Typography>

                <Typography variant="body1">
                </Typography>
              </Stack>
            }
          />
        </Grid>

        <Grid item container width={{lg: 1000,}} rowSpacing={2}>
          <GridItem label={translate('Cargo Ready')} text={displayDate(cargoReadyByDate)} isVisible={isPickupStop} />
          <GridItem label={translate('Requested')} text={displayDate(requestedDate)} isVisible={isPickupStop || isDeliverStop} />
          <GridItem label={translate('Cut Off')} text={displayDate(cutOffDate)} isVisible={isPickupStop && !!cutOffDate} />
          <GridItem label={translate('Scheduled')} text={schedDateRange} isVisible={isPickupStop && !!schedDateRange} />
          <GridItem label={translate('Estimated Arrival')} text={displayDate(estimatedArrivalDate)} isVisible={isTransitionalStop} />
          <GridItem label={translate('Arrived')} text={displayDate(arrivedDate)} isVisible={isTransitionalStop || isPickupStop} />
          <GridItem label={translate('Estimated Departure')} text={displayDate(estimatedDepartureDate)} isVisible={isPickupStop} />
          <GridItem label={translate('Departed')} text={displayDate(departedDate)} isVisible={isTransitionalStop || isPickupStop} />
          <GridItem label={translate('Estimated Delivery')} text={displayDate(estimatedDeliveryDate)} isVisible={isDeliverStop} />
          <GridItem label={translate('Delivered')} text={displayDate(departedDate)} isVisible={isDeliverStop} />
        </Grid>
      </Grid>
    </Box>
  );
}
